import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { FaRegStickyNote, FaPlus, FaPen, FaArchive, FaThumbtack, FaCheck, FaUndo, FaRegClock, FaSlidersH } from "react-icons/fa";
import { DRAFTS_CONSTANTS } from "../_MODULES/_API";
import { OPTIONS_DRAFT_ORIGIN, STATUS_MAP_DRAFT_ORIGIN, onFindOptionLabel } from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { StatusBadge } from "../../../../../_CORE/APP/Templates/_UI/Status/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FormGrid, FieldInput, FieldSelect, FieldRichText, FieldDate, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { SearchFilter } from "../../../../_UI/SearchFilter";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";

const EMPTY_VALUES = { title: "", origin: "MEETING", writtenAt: null, content: "", isPinned: false };

const onFormatDate = (value) => {

    if (!value) return "";

    return new Date(value).toLocaleString("es-AR", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
};

export const PanelDrafts = ({ subProjectId }) => {

    const [useSearch, setSearch] = useState("");
    const [useOrigin, setOrigin] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [useDraftEdit, setDraftEdit] = useState(null);
    const [useIsModalOpen, setIsModalOpen] = useState(false);

    const { apiGet: onGetDrafts, useApiData: DRAFTS, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 300,
    });

    const onLoadDrafts = () => onGetDrafts({
        endpoint: DRAFTS_CONSTANTS.LIST,
        config: {
            params: {
                subProjectId,
                search: useSearch || undefined,
                origin: useOrigin || undefined,
            },
        },
    });

    useEffect(() => {
        const TIMER = setTimeout(onLoadDrafts, 250);
        return () => clearTimeout(TIMER);
    }, [subProjectId, useSearch, useOrigin]); // eslint-disable-line react-hooks/exhaustive-deps

    const FORM = useForm({
        initialValues: EMPTY_VALUES,

        schema: { content: { required: true } },

        onSubmit: async (values) => {

            const IS_EDIT = !!useDraftEdit?.id;

            const PAYLOAD = {
                title: values.title || "",
                origin: values.origin,
                content: values.content,
                writtenAt: values.writtenAt ? new Date(values.writtenAt).toISOString() : undefined,
                isPinned: !!values.isPinned,
            };

            if (!IS_EDIT) PAYLOAD.subProjectId = subProjectId;

            const RESPONSE = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: DRAFTS_CONSTANTS.UPDATE, successMessage: "Borrador actualizado", id: useDraftEdit.id, data: PAYLOAD })
                : await actionMutate({ method: "POST", endpoint: DRAFTS_CONSTANTS.CREATE, successMessage: "Borrador guardado", data: PAYLOAD });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            mutateLocal(IS_EDIT
                ? { id: useDraftEdit.id, patch: RESPONSE.data }
                : { insert: RESPONSE.data });

            FORM.reset();
            setDraftEdit(null);
            setIsModalOpen(false);
        },
    });

    const onOpenCreate = () => {
        setDraftEdit(null);
        FORM.setValues(EMPTY_VALUES);
        setIsModalOpen(true);
    };

    const onOpenEdit = (draft) => {
        setDraftEdit(draft);
        FORM.setValues({
            title: draft.title ?? "",
            origin: draft.origin ?? "MEETING",
            writtenAt: draft.writtenAt ? new Date(draft.writtenAt) : null,
            content: draft.content ?? "",
            isPinned: !!draft.isPinned,
        });
        setIsModalOpen(true);
    };

    const onCloseModal = () => {
        setIsModalOpen(false);
        setDraftEdit(null);
    };

    const onPatchFlag = async (draft, data) => {
        const previous = Object.fromEntries(Object.keys(data).map((key) => [key, draft[key]]));
        mutateLocal({ id: draft.id, patch: data });

        const RESPONSE = await actionMutate({
            method: "PATCH", endpoint: DRAFTS_CONSTANTS.UPDATE, successMessage: "Borrador actualizado", id: draft.id, data,
        });

        if (!RESPONSE.success) {
            mutateLocal({ id: draft.id, patch: previous });
            return;
        }
        mutateLocal({ id: draft.id, patch: RESPONSE.data });
    };

    const onDelete = async (draft) => {

        const IS_CONFIRMED = await confirm.danger(
            "Archivar borrador",
            `Se archiva "${draft.title || "la nota sin título"}". Lo podés recuperar desde Archivados.`,
        );

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: DRAFTS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: draft.id }, successMessage: "Borrador archivado",
        });

        if (RESPONSE.success) mutateLocal({ id: draft.id, remove: true });
    };

    const SORTED_DRAFTS = [...(DRAFTS ?? [])].sort((a, b) =>
        (Number(!!b.isPinned) - Number(!!a.isPinned)) || String(b.writtenAt ?? b.createdAt ?? "").localeCompare(String(a.writtenAt ?? a.createdAt ?? ""))
    );
    const PAGER = usePagedList(SORTED_DRAFTS, 6, "draftsPage");

    const ORIGIN_OPTIONS = [{ value: "", label: "Todos los orígenes" }, ...OPTIONS_DRAFT_ORIGIN];

    return (
        <>
            <div className="LStudio__app_panel_bar">

                <ViewToolbar
                    filters={<>
                        <SearchFilter value={useSearch} onChange={setSearch} placeholder="Ej.: Recordar migración o configuración..." />
                        <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />}
                            onClick={() => setAdvancedOpen((current) => !current)}>
                            {useOrigin ? "Avanzado (1)" : "Avanzado"}
                        </AppButton>
                    </>}
                    expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen} activeCount={useOrigin ? 1 : 0}
                        onClear={() => setOrigin("")}>
                        <FieldSelect id="draft_origin" label="Origen" options={ORIGIN_OPTIONS} isSearchable
                            value={useOrigin} onChange={(event) => setOrigin(event.target.value)} />
                    </AdvancedFilterPanel>}
                    total={PAGER.total} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "title", label: "Título" },
                        { key: "origin", label: "Origen" },
                        { key: "writtenAt", label: "Redactado" },
                        { key: "isResolved", label: "Trabajado" },
                    ]}
                    exportRows={(DRAFTS ?? []).map((draft) => ({
                        title: draft.title || "Sin título",
                        origin: onFindOptionLabel(OPTIONS_DRAFT_ORIGIN, draft.origin),
                        writtenAt: onFormatDate(draft.writtenAt),
                        isResolved: draft.isResolved ? "Sí" : "No",
                    }))}
                    exportName="borradores"
                    actions={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo borrador</AppButton>} />

            </div>

            <p className="LStudio__app_todos_hint">
                Ideas sueltas, apuntes de una reunión, cosas por hacer que todavía no son una tarea.
                Cada borrador guarda su fecha de redactado. Cuando lo pases a bitácora o tareas, marcalo como trabajado.
            </p>

            {IS_LOADING && <Skeleton variant="box" height={200} count={2} />}

            {
                !IS_LOADING && (DRAFTS?.length ?? 0) === 0 && (
                    <EmptyState icon={<FaRegStickyNote />} title="No hay borradores"
                        description="Anotá acá lo que surja, sin ordenar. Después lo bajás a bitácora o tareas."
                        action={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo borrador</AppButton>} />
                )
            }

            {
                !IS_LOADING && DRAFTS?.length > 0 && (
                    <div className="LStudio__app_drafts_list">
                        {
                            PAGER.paged.map((draft) => (
                                <Card key={draft.id} className="LStudio__app_drafts_item" data-resolved={draft.isResolved}>

                                    <div className="LStudio__app_drafts_item_head">

                                        <h3 className="LStudio__app_drafts_item_title">
                                            {draft.isPinned && <FaThumbtack aria-hidden="true" />}
                                            {draft.title || "Sin título"}
                                        </h3>

                                        <StatusBadge status={draft.origin} map={STATUS_MAP_DRAFT_ORIGIN} size="sm" />

                                        {draft.isResolved && <Badge tone="ok" size="sm">Trabajado</Badge>}

                                    </div>

                                    <p className="LStudio__app_drafts_item_date">
                                        <FaRegClock aria-hidden="true" /> Redactado el {onFormatDate(draft.writtenAt)}
                                    </p>

                                    <div className="LStudio__app_drafts_item_content"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(draft.content ?? "") }} />

                                    <div className="LStudio__app_drafts_item_actions">

                                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaThumbtack />}
                                            onClick={() => onPatchFlag(draft, { isPinned: !draft.isPinned })}>
                                            {draft.isPinned ? "Quitar destacado" : "Destacar"}
                                        </AppButton>

                                        <AppButton size="sm" variant="ghost" tone="neutral"
                                            icon={draft.isResolved ? <FaUndo /> : <FaCheck />}
                                            onClick={() => onPatchFlag(draft, { isResolved: !draft.isResolved })}>
                                            {draft.isResolved ? "Reabrir" : "Marcar trabajado"}
                                        </AppButton>

                                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaPen />}
                                            onClick={() => onOpenEdit(draft)}>Editar</AppButton>

                                        <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />}
                                            onClick={() => onDelete(draft)}>Archivar</AppButton>

                                    </div>

                                </Card>
                            ))
                        }
                    </div>
                )
            }

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <AppModal isOpen={useIsModalOpen} onClose={onCloseModal}
                isDirty={FORM.isDirty} size="xl"
                title={useDraftEdit ? "Editar borrador" : "Nuevo borrador"}
                description="Escribí libre. Si no ponés fecha se guarda la de hoy."
                footer={
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={onCloseModal}>Cancelar</AppButton>
                        <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                            {useDraftEdit ? "Guardar cambios" : "Guardar borrador"}
                        </AppButton>
                    </>
                } >

                <div className="LStudio__app_priority_toggle">
                    <FieldSwitch id="draft_isPinned" label="Destacado" text="Fijar este borrador arriba"
                        isChecked={!!FORM.values.isPinned}
                        onChange={(event) => FORM.setValue("isPinned", event.target.checked)} />
                </div>

                <FormGrid columns={3}>

                    <FieldInput {...FORM.field("title")} label="Título" placeholder="Ej: Reunion con el equipo" />

                    <FieldSelect {...FORM.field("origin")} label="Origen" options={OPTIONS_DRAFT_ORIGIN} isSearchable />

                    <FieldDate {...FORM.fieldValue("writtenAt", { emptyValue: null })} label="Fecha de redactado"
                        showTime hint="Vacío guarda la fecha y hora de ahora" />

                    <FieldRichText {...FORM.fieldValue("content", { emptyValue: "" })} label="Notas" minHeight={260}
                        placeholder="Puntos de la reunión, ideas, pendientes…" span={3} />

                </FormGrid>

            </AppModal>
        </>
    );
};
